import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CheckSquare as CheckSquareIcon,
  FileText as FileTextIcon,
  HandPalm as HandPalmIcon,
  Square as SquareIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";

type Document = { label: string; native: boolean; pdf: boolean };

type Item = {
  transmissionId: number;
  slipId: number;
  slipNumber: string | null;
  createdAt: string;
  recipientTeamId: number;
  recipientName: string;
  documents: Document[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: Item[] };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR");
}

function formatFormats(doc: Document): string {
  const parts: string[] = [];
  if (doc.native) parts.push("Natif");
  if (doc.pdf) parts.push("PDF");
  return parts.length > 0 ? ` (${parts.join(" / ")})` : "";
}

function documentsTitle(documents: Document[]): string {
  return documents.map((d) => `${d.label}${formatFormats(d)}`).join("\n");
}

/**
 * Écran 022 — « Enregistrer la mise au courrier effective de bordereaux ».
 *
 * Tableau des exemplaires (une transmission courrier par destinataire) non
 * encore mis au courrier, trié par destinataire puis n° de bordereau. Chaque
 * ligne affiche la date de création, le n° de bordereau et les pièces à
 * joindre réellement persistées (version_transmission), avec leurs formats
 * (Natif / PDF). Le picto à cocher enregistre la mise au courrier effective
 * (pose `dateEnvoi`) ; l'action est idempotente côté API et la ligne quitte la
 * file une fois envoyée.
 *
 * Depuis la même file, un exemplaire prévu par courrier peut néanmoins
 * être enregistré comme remis en main propre. Après confirmation (le mode
 * d'envoi initialement prévu est modifié), l'API bascule `modeEnvoi` vers
 * « en_main_propre » et enregistre la remise (`dateEnvoi`,
 * `sentByEmployeeId`) ; la ligne quitte la file.
 *
 * Non représenté (non persisté) : quantités papier, PDF généré du
 * bordereau. Aucun writer ne pose encore `modeEnvoi = courrier`, donc la file
 * reste vide tant que l'étape de choix du mode d'envoi n'existe pas.
 */
export function MisesAuCourrierEffective() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (isNil(yardId)) return;

    let cancelled = false;

    async function fetchExemplaires() {
      setState({ status: "loading" });

      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/mises-au-courrier/exemplaires",
          { params: { path: { yardId: yardId ?? "" } } },
        );

        if (cancelled) return;

        if (error || isNil(data)) {
          setState({
            status: "error",
            error: "Erreur lors du chargement des mises au courrier",
          });
          return;
        }

        setState({ status: "success", items: data.data.items });
      } catch {
        if (cancelled) return;
        setState({
          status: "error",
          error: "Erreur lors du chargement des mises au courrier",
        });
      }
    }

    fetchExemplaires();
    return () => {
      cancelled = true;
    };
  }, [yardId]);

  async function markSent(transmissionId: number) {
    if (isNil(yardId) || isNotNil(sendingId)) return;
    setSendingId(transmissionId);
    setActionError(null);

    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/mises-au-courrier/exemplaires/{transmissionId}/envoyer",
        {
          params: {
            path: { yardId, transmissionId },
          },
        },
      );

      if (error) {
        setActionError("Impossible d'enregistrer la mise au courrier");
        return;
      }

      // L'exemplaire quitte la file des envois en attente.
      setState((prev) =>
        prev.status === "success"
          ? {
              status: "success",
              items: prev.items.filter(
                (i) => i.transmissionId !== transmissionId,
              ),
            }
          : prev,
      );
    } catch {
      setActionError("Impossible d'enregistrer la mise au courrier");
    } finally {
      setSendingId(null);
    }
  }

  // Enregistre que la remise s'est finalement faite en main propre.
  // Le mode d'envoi initialement prévu est modifié : confirmation explicite,
  // comme pour les conversions de mode des écrans 021bis / 021ter.
  async function markHandDelivered(item: Item) {
    if (isNil(yardId) || isNotNil(sendingId)) return;

    const slipLabel = item.slipNumber ?? `Bordereau ${item.slipId}`;
    const confirmed = await confirmDialog({
      title: "Enregistrer la remise en main propre",
      message: `Enregistrer la remise en main propre de ${slipLabel} à ${item.recipientName} ? Le mode d'envoi initialement prévu (courrier) sera modifié.`,
      confirmLabel: "Enregistrer",
    });
    if (!confirmed) return;

    setSendingId(item.transmissionId);
    setActionError(null);

    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/mises-au-courrier/exemplaires/{transmissionId}/remettre-en-main-propre",
        {
          params: {
            path: { yardId, transmissionId: item.transmissionId },
          },
        },
      );

      if (error) {
        setActionError("Impossible d'enregistrer la remise en main propre");
        return;
      }

      // L'exemplaire n'est plus un courrier : il quitte la file.
      setState((prev) =>
        prev.status === "success"
          ? {
              status: "success",
              items: prev.items.filter(
                (i) => i.transmissionId !== item.transmissionId,
              ),
            }
          : prev,
      );
    } catch {
      setActionError("Impossible d'enregistrer la remise en main propre");
    } finally {
      setSendingId(null);
    }
  }

  const items = state.status === "success" ? state.items : [];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Enregistrer les mises au courrier effectives
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Cochez un exemplaire une fois sa mise au courrier effectuée, ou
          enregistrez sa remise en main propre si le bordereau a finalement été
          remis directement.
        </p>
        {isNotNil(actionError) ? (
          <p className="mt-2 text-sm text-red-600">{actionError}</p>
        ) : null}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {/*
              Règles écran : table à disposition fixe, colonnes « Destinataire »
              et « Documents à joindre » élastiques, colonnes fixes centrées,
              hauteur de ligne fixe avec troncature (détail au survol), seul le
              corps de table défile verticalement sous un en-tête collant.
            */}
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col />
                <col className="w-32" />
                <col className="w-32" />
                <col />
                <col className="w-24" />
                <col className="w-28" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Destinataire
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Bordereau
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Date de création
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Documents à joindre
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Mise au courrier
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Remise en main propre
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-sm text-zinc-500"
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-12 text-center text-sm text-zinc-500"
                    >
                      Aucune mise au courrier en attente
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const slipLabel =
                      item.slipNumber ?? `Bordereau ${item.slipId}`;
                    const docsSummary = item.documents
                      .map((d) => d.label)
                      .join(", ");
                    const isSending = sendingId === item.transmissionId;
                    return (
                      <tr
                        key={item.transmissionId}
                        className="h-10 border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                      >
                        <td className="truncate px-3 text-left text-zinc-700 dark:text-zinc-300">
                          <span title={item.recipientName}>
                            {item.recipientName || "-"}
                          </span>
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={slipLabel}>{slipLabel}</span>
                        </td>
                        <td className="truncate px-3 text-left text-zinc-700 dark:text-zinc-300">
                          {formatDate(item.createdAt)}
                        </td>
                        <td className="truncate px-3 text-left text-zinc-700 dark:text-zinc-300">
                          {item.documents.length > 0 ? (
                            <span
                              className="inline-flex items-center gap-1"
                              title={documentsTitle(item.documents)}
                            >
                              <FileTextIcon
                                size={16}
                                weight="fill"
                                className="shrink-0"
                              />
                              <span className="truncate">{docsSummary}</span>
                            </span>
                          ) : (
                            <span className="text-zinc-300">-</span>
                          )}
                        </td>
                        <td className="px-3 text-center">
                          <button
                            type="button"
                            onClick={() => markSent(item.transmissionId)}
                            disabled={isSending}
                            aria-label={`Enregistrer la mise au courrier de ${slipLabel} vers ${item.recipientName}`}
                            title="Enregistrer la mise au courrier"
                            className={cn(
                              "inline-flex items-center justify-center rounded p-1 text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400",
                              isSending && "opacity-50",
                            )}
                          >
                            {isSending ? (
                              <CheckSquareIcon size={20} weight="fill" />
                            ) : (
                              <SquareIcon size={20} />
                            )}
                          </button>
                        </td>
                        <td className="px-3 text-center">
                          <button
                            type="button"
                            onClick={() => markHandDelivered(item)}
                            disabled={isSending}
                            aria-label={`Enregistrer la remise en main propre de ${slipLabel} à ${item.recipientName}`}
                            title="Enregistrer la remise en main propre"
                            className={cn(
                              "inline-flex items-center justify-center rounded p-1 text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400",
                              isSending && "opacity-50",
                            )}
                          >
                            <HandPalmIcon size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
